import React, { useState, useEffect, useRef } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper, 
  Divider, 
  CircularProgress, 
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  CardActions,
  Button,
  useTheme,
  useMediaQuery,
  Link
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import axios from 'axios';
import ContactButtons from '../components/common/ContactButtons';
const AboutPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [contactData, setContactData] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch contact information from settings
        const settingsResponse = await axios.get('/api/settings');
        if (settingsResponse.data) {
          setContactData({
            phone: settingsResponse.data.contactPhone || '+7 (999) 123-45-67',
            email: settingsResponse.data.contactEmail || 'info@domiktyumen.ru',
            address: settingsResponse.data.contactAddress || 'г. Тюмень, ул. Примерная, 123',
            workingHours: settingsResponse.data.workingHours || 'Пн-Пт: 9:00-18:00, Сб-Вс: 10:00-16:00'
          });
        }
        
        // Получаем данные об объектах из API
        try {
          const propertiesResponse = await axios.get('/api/properties');
          
          if (propertiesResponse.data && propertiesResponse.data.length > 0) {
            // Преобразуем данные объектов в формат для карты
            // Используем реальные координаты объектов, если они есть, иначе используем координаты Тюмени
            const tyumenCoordinates = [57.152985, 65.561989]; // Координаты Тюмени по умолчанию
            
            const locationData = propertiesResponse.data.map(property => {
              // Проверяем наличие корректных координат
              const hasValidCoordinates = property.latitude && property.longitude && 
                                         !isNaN(parseFloat(property.latitude)) && 
                                         !isNaN(parseFloat(property.longitude));
              
              // Используем координаты объекта, если они есть, иначе используем координаты Тюмени
              const coordinates = hasValidCoordinates ? 
                [parseFloat(property.latitude), parseFloat(property.longitude)] : 
                tyumenCoordinates;
              
              return {
                id: property._id || property.id,
                name: property.name,
                address: property.address,
                coordinates: coordinates
              };
            });
            
            setLocations(locationData);
          } else {
            // Если API не вернул объекты, используем данные по умолчанию
            setLocations([
              {
                id: 1,
                name: 'Загородный дом "Сосновый бор"',
                address: 'Тюменская область, 20 км от города, пос. Сосновка',
                coordinates: [57.152985, 65.561989]
              },
              {
                id: 2,
                name: 'Коттедж "Озерный"',
                address: 'Тюменская область, 15 км от города, д. Озерки',
                coordinates: [57.121234, 65.543210]
              }
            ]);
          }
        } catch (propertiesError) {
          console.error('Ошибка при получении объектов:', propertiesError);
          // В случае ошибки используем данные по умолчанию
          setLocations([
            {
              id: 1,
              name: 'Загородный дом "Сосновый бор"',
              address: 'Тюменская область, 20 км от города, пос. Сосновка',
              coordinates: [57.152985, 65.561989]
            },
            {
              id: 2,
              name: 'Коттедж "Озерный"',
              address: 'Тюменская область, 15 км от города, д. Озерки',
              coordinates: [57.121234, 65.543210]
            }
          ]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Не удалось загрузить данные. Пожалуйста, попробуйте позже.');
        
        // Set default data on error
        setContactData({
          phone: '+7 (999) 123-45-67',
          email: 'info@domiktyumen.ru',
          address: 'г. Тюмень, ул. Примерная, 123',
          workingHours: 'Пн-Пт: 9:00-18:00, Сб-Вс: 10:00-16:00'
        });
        
        setLocations([
          {
            id: 1,
            name: 'Загородный дом "Сосновый бор"',
            address: 'Тюменская область, 20 км от города, пос. Сосновка',
            coordinates: [57.152985, 65.561989]
          },
          {
            id: 2,
            name: 'Коттедж "Озерный"',
            address: 'Тюменская область, 15 км от города, д. Озерки',
            coordinates: [57.121234, 65.543210]
          }
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Переменная для хранения ссылки на карту
  const mapRef = useRef(null);
  
  // Функция для преобразования адреса в координаты (OSM Nominatim)
  const geocodeAddress = async (address) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`);
      const data = await response.json();
      if (Array.isArray(data) && data.length > 0) {
        const item = data[0];
        return [parseFloat(item.lat), parseFloat(item.lon)];
      }
      return null;
    } catch (error) {
      console.error('Ошибка геокодирования:', error);
      return null;
    }
  };
  
  // Initialize 2GIS map after component mount and DOM is ready
  useEffect(() => {
    // Функция очистки карты
    const cleanupMap = () => {
      try {
        if (mapRef.current) {
          mapRef.current.remove();
          mapRef.current = null;
        }
      } catch (e) {
        console.error('Error cleaning up map:', e);
      }
    };
    
    // Очистка предыдущей карты при повторной инициализации
    cleanupMap();
    
    if (!loading && locations.length > 0) {
      // Проверяем, существует ли контейнер для карты
      const mapContainer = document.getElementById('map-container');
      if (!mapContainer) {
        console.error('Map container not found. Map initialization skipped.');
        return;
      }
      
      const initMap = () => {
        try {
          // Проверяем, что контейнер карты существует
          if (!document.getElementById('map-container')) {
            console.error('Map container not found during initialization');
            return;
          }
          
          // Создаем карту только если она еще не создана
          if (!mapRef.current) {
            console.log('Initializing Leaflet map with center:', locations[0].coordinates);
            
            // Используем координаты Тюмени по умолчанию, если координаты некорректны
            const defaultCenter = [57.152985, 65.561989];
            const center = Array.isArray(locations[0].coordinates) && 
                          locations[0].coordinates.length === 2 && 
                          !isNaN(locations[0].coordinates[0]) && 
                          !isNaN(locations[0].coordinates[1]) ? 
                          locations[0].coordinates : defaultCenter;
            
            // Initialize Leaflet map without default attribution control
            mapRef.current = window.L.map('map-container', { attributionControl: false }).setView(center, 11);
            // Add custom attribution control without prefix or emojis
            window.L.control.attribution({ prefix: false }).addTo(mapRef.current);
            window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
              attribution: '© OpenStreetMap contributors'
            }).addTo(mapRef.current);

            // Add markers for each location
            locations.forEach(location => {
              // Проверяем корректность координат
              if (Array.isArray(location.coordinates) && 
                  location.coordinates.length === 2 && 
                  !isNaN(location.coordinates[0]) && 
                  !isNaN(location.coordinates[1])) {
                
                const marker = window.L.marker(location.coordinates).addTo(mapRef.current);
                
                // Add popup with location info and 2GIS link button
                const popupContent = `
                  <div style="text-align: center;">
                    <strong>${location.name}</strong><br>
                    ${location.address}<br><br>
                    <a href="https://2gis.ru/tyumen/search/${encodeURIComponent(location.address)}" 
                       target="_blank" 
                       style="display: inline-block; background-color: #3498db; color: white; padding: 8px 12px; 
                              text-decoration: none; border-radius: 4px; font-weight: bold;">
                      Открыть в 2ГИС
                    </a>
                  </div>
                `;
                marker.bindPopup(popupContent, { maxWidth: 300 });
              } else {
                console.error('Invalid coordinates for location:', location);
              }
            });
          }
        } catch (e) {
          console.error('Error initializing 2GIS map:', e);
        }
      };
      
      // Ensure Leaflet CSS is loaded
      const ensureLeafletCss = () => {
        if (!document.querySelector('link[href*="leaflet.css"]')) {
          const link = document.createElement('link');
          link.rel = 'stylesheet';
          link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
          document.head.appendChild(link);
        }
      };

      // Load Leaflet if not already loaded
      if (window.L) {
        ensureLeafletCss();
        initMap();
      } else {
        ensureLeafletCss();
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.async = true;
        script.onload = () => initMap();
        script.onerror = () => {
          console.error('Failed to load Leaflet script');
        };
        document.body.appendChild(script);
      }
    }
    
    // Cleanup function
    return cleanupMap;
  }, [loading, locations]);
  
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }
  
  return (
    <Box sx={{ py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 6, textAlign: 'center' }}>
          <Typography 
            variant="h2" 
            component="h1" 
            sx={{ 
              fontWeight: 700,
              mb: 2,
              position: 'relative',
              display: 'inline-block',
              '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -10,
                left: '50%',
                width: '80px',
                height: '4px',
                backgroundColor: 'primary.main',
                transform: 'translateX(-50%)'
              }
            }}
          >
            Контактная информация
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mt: 3, maxWidth: '800px', mx: 'auto' }}>
            Свяжитесь с нами для бронирования или получения дополнительной информации о наших объектах
          </Typography>
        </Box>
        
        {/* Contact Information */}
        <Grid container spacing={4} sx={{ mb: 6 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: '12px' }}>
              <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
                Наши контакты
              </Typography>
              
              <List>
                {contactData?.phone && (
                  <ListItem disableGutters>
                    <ListItemIcon>
                      <PhoneIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Телефон" 
                      secondary={contactData.phone} 
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                    />
                  </ListItem>
                )}
                
                {contactData?.email && (
                  <ListItem disableGutters>
                    <ListItemIcon>
                      <EmailIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Email" 
                      secondary={contactData.email}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                    />
                  </ListItem>
                )}
                
                {contactData?.address && (
                  <ListItem disableGutters>
                    <ListItemIcon>
                      <LocationOnIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Адрес офиса" 
                      secondary={contactData.address}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                    />
                  </ListItem>
                )}
                
                {contactData?.workingHours && (
                  <ListItem disableGutters>
                    <ListItemIcon>
                      <AccessTimeIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Часы работы" 
                      secondary={contactData.workingHours}
                      primaryTypographyProps={{ variant: 'subtitle2', color: 'text.secondary' }}
                      secondaryTypographyProps={{ variant: 'body1', fontWeight: 500 }}
                    />
                  </ListItem>
                )}
              </List>
              
              {/* Кнопки связи */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 500 }}>
                  Быстрая связь с нами:
                </Typography>
                <ContactButtons spacing={3} />
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 4, height: '100%', borderRadius: '12px' }}>
              <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
                О нас
              </Typography>
              <Typography variant="body1" paragraph>
                DomikTyumen - это сервис аренды загородной недвижимости в Тюмени и Тюменской области. Мы предлагаем комфортабельные коттеджи, традиционные русские бани и уютные дачи для отдыха с семьей и друзьями.
              </Typography>
              <Typography variant="body1" paragraph>
                Наша миссия - сделать ваш отдых незабываемым. Все наши объекты тщательно отобраны и поддерживаются в отличном состоянии. Мы гарантируем чистоту, комфорт и безопасность на всех наших объектах.
              </Typography>
              <Typography variant="body1">
                Выбирайте любой из наших объектов и наслаждайтесь отдыхом на природе в любое время года!
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Locations */}
        <Typography variant="h4" component="h2" sx={{ mb: 4, fontWeight: 700 }}>
          Наши объекты
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 5 }}>
          {locations.map((location) => (
            <Grid item xs={12} sm={6} md={4} key={location.id}>
              <Card elevation={2} sx={{ height: '100%', borderRadius: '12px', transition: 'transform 0.3s, box-shadow 0.3s', '&:hover': { transform: 'translateY(-5px)', boxShadow: '0 10px 30px rgba(0,0,0,0.1)' } }}>
                <CardContent>
                  <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 1 }}>
                    {location.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mt: 2 }}>
                    <LocationOnIcon color="primary" sx={{ mt: 0.3, mr: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      {location.address}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ flexDirection: 'column', gap: 1, p: 2 }}>
                  <Button 
                    variant="outlined" 
                    fullWidth
                    component={Link}
                    href={`https://2gis.ru/tyumen/search/${encodeURIComponent(location.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ justifyContent: 'flex-start', mb: 1 }}
                    startIcon={
                      <Box 
                        component="img" 
                        src="/assets/resources_logos/2gis.jpg" 
                        alt="2GIS" 
                        sx={{ height: 24, width: 'auto' }} 
                      />
                    }
                  >
                    2ГИС
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth
                    component={Link}
                    href={`https://yandex.ru/maps/?text=${encodeURIComponent(location.address)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ justifyContent: 'flex-start', mb: 1 }}
                    startIcon={
                      <Box 
                        component="img" 
                        src="/assets/resources_logos/yandex.png" 
                        alt="Яндекс" 
                        sx={{ height: 24, width: 'auto' }} 
                      />
                    }
                  >
                    Яндекс.Карты
                  </Button>
                  <Button 
                    variant="outlined" 
                    fullWidth
                    component={Link}
                    href={`https://www.avito.ru/tyumenskaya_oblast?q=${encodeURIComponent(location.name)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ justifyContent: 'flex-start' }}
                    startIcon={
                      <Box 
                        component="img" 
                        src="/assets/resources_logos/avito.png" 
                        alt="Авито" 
                        sx={{ height: 24, width: 'auto' }} 
                      />
                    }
                  >
                    Авито
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
        
        {/* Map */}
        <Paper elevation={3} sx={{ borderRadius: '12px', overflow: 'hidden', mb: 6 }}>
          <Typography variant="h5" component="h2" sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
            Карта объектов
          </Typography>
          <Box 
            id="map-container" 
            sx={{ 
              height: '500px', 
              width: '100%',
              '& .dg-popup__container': { 
                color: '#333' 
              } 
            }} 
          />
        </Paper>
      </Container>
    </Box>
  );
};

export default AboutPage;
